import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import CustomTextInput from '../../../CustomTextInput';
import { FormQuestionWithOptions } from '../../../../services/apiSLTP/qhse/forms/formQuestions';
import { questionTypeEnum } from '../../../../utils/enums';
import {
    CreateFormQuestionOptionBody,
    UpdateFormQuestionOptionBody,
    MoveFormQuestionOptionBody,
} from '../../../../services/apiSLTP/qhse/forms/formQuestionOptions';
import { useDrag, useDrop } from 'react-dnd';
import Icon from '../../../shared/Icon';
import IconButton from '../../../shared/IconButton';
import OptionItem from './OptionItem';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { useTranslation } from 'react-i18next';

interface QuestionTypeEditorProps {
    question: FormQuestionWithOptions;
    onAddQuestionOption: (data: CreateFormQuestionOptionBody) => void;
    onDeleteQuestionOption: (optionId: string) => void;
    onUpdateQuestionOption: (optionId: string, data: UpdateFormQuestionOptionBody) => void;
    onMoveQuestionOption: (optionId: string, data: MoveFormQuestionOptionBody) => void;
}
const QuestionTypeEditor: React.FC<QuestionTypeEditorProps> = ({
    question,
    onAddQuestionOption,
    onDeleteQuestionOption,
    onUpdateQuestionOption,
    onMoveQuestionOption,
}) => {
    const { t } = useTranslation();
    const [localOptions, setLocalOptions] = useState(question.options || []);

    useEffect(() => {
        setLocalOptions(question.options || []);
    }, [question.options]);

    const moveOptionLocal = useCallback(
        (fromIndex: number, toIndex: number) => {
            const updatedOptions = [...localOptions];
            const [moved] = updatedOptions.splice(fromIndex, 1);
            updatedOptions.splice(toIndex, 0, moved);
            setLocalOptions(updatedOptions);
        },
        [localOptions]
    );

    const handleDrop = () => {
        localOptions.forEach((opt, idx) => {
            onMoveQuestionOption(opt._id, { newOrder: idx });
        });
    };

    if (question.type === questionTypeEnum.MULTIPLE_CHOICE || question.type === questionTypeEnum.SINGLE_CHOICE) {
        return (
            <Box display="flex" flexDirection="column" alignItems={'flex-start'} gap={1}>
                {localOptions.map((opt, idx) => (
                    <OptionItem
                        key={opt._id}
                        option={opt}
                        index={idx}
                        questionType={question.type}
                        moveOption={moveOptionLocal}
                        onUpdateOption={onUpdateQuestionOption}
                        onDeleteOption={onDeleteQuestionOption}
                        onDrop={(newOrder) => onMoveQuestionOption(opt._id, { newOrder })}
                        localOptions={localOptions}
                    />
                ))}
                <Button
                    onClick={() =>
                        onAddQuestionOption({
                            questionId: question._id,
                            value: `${capitalizeFirstLetter(t('option'))} ${localOptions.length + 1}`,
                        })
                    }
                    startIcon={<Icon name="addCircle" />}
                    sx={{ marginLeft: 4.3 }}
                    color="info"
                >
                    {capitalizeFirstLetter(t('addOption'))}
                </Button>
            </Box>
        );
    }

    return <CustomTextInput multiline={question.type === questionTypeEnum.TEXT_LONG} placeholder="Saisir votre texte…" disabled />;
};

export default QuestionTypeEditor;
